import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import io from 'socket.io-client';
import './GuestConference.css';
import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';

// Request camera + mic permissions on native platforms before getUserMedia
const requestMediaPermissions = async () => {
  if (!Capacitor.isNativePlatform()) return true;

  try {
    const { camera, microphone } = await Camera.requestPermissions({ permissions: ['camera', 'microphone'] });
    console.log('Permission status — camera:', camera, 'microphone:', microphone);
    if (camera === 'denied' || microphone === 'denied') {
      alert('Camera and microphone access are required for video conferencing. Please enable them in your device settings.');
      return false;
    }
    return true;
  } catch (err) {
    console.warn('Permission request error:', err);
    return true;
  }
};

const GuestConference = () => {
  const [localStream, setLocalStream] = useState(null);
  const guestNameRef = useRef('');
  const [remoteStreams, setRemoteStreams] = useState([]);
  const roomRef = useRef('');
  const [hostStream, setHostStream] = useState(null); // Host's video stream
  const [isJoinedCall, setIsJoinedCall] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [room, setRoom] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isWaitingForAdmission, setIsWaitingForAdmission] = useState(false); // NEW STATE
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const iceCandidateQueue = useRef({});
  const peerConnections = useRef({});
  const socketRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef({});
  const location = useLocation();
  const [callEnded, setCallEnded] = useState(false);
  const [callEndReason, setCallEndReason] = useState('');
  const [remoteMutedStates, setRemoteMutedStates] = useState({});

useEffect(() => {
  const queryParams = new URLSearchParams(location.search);
  const roomParam = queryParams.get('room');
  if (!roomParam) {
    console.error('Room parameter is missing in URL.');
    return;
  }

  setRoom(roomParam);
  roomRef.current = roomParam; // Store in ref too
  console.log('Guest connecting to room:', roomParam);

  const socket = io.connect('https://api.airflyp.com');
  socketRef.current = socket;

  if (!socket) {
    console.error('Failed to initialize socket connection.');
    return;
  }

  socket.on('connect', () => {
    console.log(`Guest connected with socket ID: ${socket.id}`);
    // DON'T join room here - wait for admission request
  });

socket.on('admitted-to-call', () => {
  console.log('Admitted to the call by host - now joining');
  setIsWaitingForAdmission(false);
  joinCall(); // Remove the guestName check
});

socket.on('existing-users', ({ users }) => {
  console.log('Existing users in the room (Guest):', users);
  users.forEach((user) => {
    console.log(`Creating peer connection for existing user: ${user.userId}`);
    const pc = createPeerConnection(user.userId);
    // Add tracks immediately, not in setTimeout
    addLocalTracks(pc, user.userId);
  });
});

  socket.on('user-joined', ({ userId, userName }) => {
    console.log(`New user joined (Guest): ${userId} (${userName || 'Unknown'})`);
    if (isJoinedCall && localStream) {
      console.log(`Creating peer connection for new user: ${userId}`);
      const pc = createPeerConnection(userId);
      addLocalTracks(pc, userId);
    } else {
      console.log('Not ready to handle new user yet (not joined or no stream)');
    }
  });

  socket.on('offer', ({ offer, from, fromName }) => {
    console.log(`Received offer from user: ${from} (${fromName || 'Unknown'})`);
    // Only handle offers if we're actually in the call
   // if (isJoinedCall) {
      handleOffer(offer, from, fromName);
   // }
  });

  socket.on('answer', ({ answer, from }) => {
    console.log(`Received answer from user: ${from}`);
    if (isJoinedCall) {
      handleAnswer(answer, from);
    }
  });

  socket.on('ice-candidate', ({ candidate, from }) => {
    console.log(`ICE candidate received from ${from}`);
    if (isJoinedCall) {
      handleICECandidate(candidate, from);
    }
  });

socket.on('end-call', () => {
  console.log('Host ended the call.');
  
  // Set call ended state
  setCallEnded(true);
  setCallEndReason('The host has ended the call');
  
  // Clear streams
  setHostStream(null);
  setRemoteStreams([]);
  
  // Clean up peer connections
  Object.values(peerConnections.current).forEach((pc) => {
    if (pc.signalingState !== 'closed') {
      pc.close();
    }
  });
  peerConnections.current = {};
  
  // Stop local stream
  if (localStream) {
    localStream.getTracks().forEach((track) => track.stop());
    setLocalStream(null);
  }
  
  setIsJoinedCall(false);
});

  return () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  };
}, [location.search]);

useEffect(() => {
  if (socketRef.current) {
    socketRef.current.on('mute-all-guests', () => {
      console.log('Mute-all-guests event received. Muting local audio.');
      if (localStream) {
        localStream.getAudioTracks().forEach((track) => {
          track.enabled = false;
        });
      }
      setIsAudioEnabled(false);
      setRemoteMutedStates((prevState) => {
        const updatedStates = {};
        Object.keys(peerConnections.current).forEach((userId) => {
          updatedStates[userId] = true;
        });
        return { ...prevState, ...updatedStates };
      });
    });
  }
  return () => {
    if (socketRef.current) {
      socketRef.current.off('mute-all-guests');
    }
  };
}, [localStream]);

// Updated requestAdmission function to use refs
// Updated requestAdmission: use state instead of undefined refs
const requestAdmission = () => {
  const currentName = guestName.trim();
  if (!currentName) {
    alert('Please enter your name');
    return;
  }

  // Store the name in the ref so it's available later
  guestNameRef.current = currentName;

  if (socketRef.current) {
    console.log(`Guest ${currentName} requesting admission to room ${room}`);
    socketRef.current.emit('request-admission', { 
      room,
      guestName: currentName,
      guestId: socketRef.current.id
    });
    setIsModalOpen(false);
    setIsWaitingForAdmission(true);
  }
};

const handleICECandidate = (candidate, from) => {
  const pc = peerConnections.current[from];
  if (pc) {
    if (pc.remoteDescription && pc.remoteDescription.type) {
      console.log(`Adding ICE candidate directly for ${from}`);
      pc.addIceCandidate(new RTCIceCandidate(candidate)).catch((error) => {
        console.error(`Error adding ICE candidate from ${from}:`, error);
      });
    } else {
      console.log(`Queueing ICE candidate for ${from} (remote description not set yet)`);
      if (!iceCandidateQueue.current[from]) {
        iceCandidateQueue.current[from] = [];
      }
      iceCandidateQueue.current[from].push(candidate);
    }
  } else {
    console.error(`PeerConnection not found for user: ${from}`);
  }
};

const handleOffer = async (offer, from, fromName = null) => {
  try {
    const pc = peerConnections.current[from] || createPeerConnection(from);
    await pc.setRemoteDescription(new RTCSessionDescription(offer));

    // Update remote streams with the correct name when we get the offer
    if (fromName) {
      setRemoteStreams((prevStreams) => {
        const updatedStreams = prevStreams.map((stream) => 
          stream.userId === from 
            ? { ...stream, name: fromName }
            : stream
        );
        return updatedStreams;
      });
    }

    if (iceCandidateQueue.current[from]) {
      console.log(`Processing queued ICE candidates for ${from}`);
      while (iceCandidateQueue.current[from].length > 0) {
        const candidate = iceCandidateQueue.current[from].shift();
        await pc.addIceCandidate(new RTCIceCandidate(candidate)).catch((error) => {
          console.error(`Error adding queued ICE candidate from ${from}:`, error);
        });
      }
    }

    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    socketRef.current.emit('answer', { answer, to: from });
  } catch (error) {
    console.error('Error handling offer:', error);
  }
};

const handleAnswer = (answer, from) => {
  const pc = peerConnections.current[from];
  if (pc) {
    pc.setRemoteDescription(new RTCSessionDescription(answer)).catch((error) => {
      console.error('Error setting remote description:', error);
    });
  }
};

// UPDATED joinCall - more robust name handling
// Replace your current joinCall function with this:
const joinCall = async () => {
  if (isJoinedCall) {
    console.warn("Already in the call. Ignoring duplicate join attempt.");
    return;
  }

  try {
    // Use the stored values from refs
    const currentName = guestNameRef.current || 'Guest';
    const currentRoom = roomRef.current; // Use the room ref
    
    console.log(`Starting joinCall for guest: ${currentName}`);
    
    const hasPermission = await requestMediaPermissions();
    if (!hasPermission) return;
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    setLocalStream(stream);

    if (localVideoRef.current) {
      localVideoRef.current.srcObject = stream;
    }

    console.log(`Got local stream, now joining room ${currentRoom}`);

    // Now actually join the room with media
    if (socketRef.current) {
      socketRef.current.emit('join-room', { 
        room: currentRoom, 
        name: currentName, 
        guestId: socketRef.current.id
      });
      console.log(`Guest ${currentName} joining room ${currentRoom} with media`);
    }

    // Set joined state FIRST
    setIsJoinedCall(true);

    // THEN add tracks to any existing peer connections after a short delay


  } catch (error) {
    console.error('Error joining call:', error);
  }
};

const createPeerConnection = (userId) => {
  const pc = new RTCPeerConnection({
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }],
  });

  pc.onicecandidate = (event) => {
    if (event.candidate) {
      socketRef.current.emit('ice-candidate', { candidate: event.candidate, to: userId });
    }
  };

  pc.ontrack = (event) => {
    console.log(`Received remote stream from user ${userId}:`, event.streams);
  
    if (event.streams.length === 0) {
      console.warn(`No streams received from user: ${userId}`);
      return;
    }
  
    const [remoteStream] = event.streams;
  
    setRemoteStreams((prevStreams) => {
      // Check if stream already exists
      if (prevStreams.some((s) => s.userId === userId)) {
        console.warn(`Stream from userId ${userId} is already added.`);
        return prevStreams;
      }
      
      // Add stream with placeholder name - will be updated when we get offer/answer with fromName
      return [...prevStreams, { 
        userId, 
        stream: remoteStream, 
        name: `User ${userId.slice(0, 6)}` // Temporary name
      }];
    });
  
    if (remoteVideoRefs.current[userId]) {
      remoteVideoRefs.current[userId].srcObject = remoteStream;
    }
  };

  peerConnections.current[userId] = pc;
  return pc;
};

// UPDATED addLocalTracks to handle the case where we have a stream
const addLocalTracks = (pc, userId) => {
  // Try to get stream from video element first, fallback to state
  const localVideo = localVideoRef.current;
  const streamToUse = localVideo?.srcObject || localStream;
  
  if (!streamToUse) {
    console.warn("No local stream available to add tracks.");
    return;
  }

  console.log(`Adding tracks from local stream to peer connection for ${userId}`);
  streamToUse.getTracks().forEach((track) => {
    const existingSender = pc.getSenders().find((sender) => sender.track === track);
    if (!existingSender) {
      pc.addTrack(track, streamToUse);
      console.log(`Added track ${track.kind} to peer connection for user ${userId}`);
    } else {
      console.warn(`Track ${track.kind} already added for user ${userId}`);
    }
  });
};

const leaveCall = () => {
  console.log('Guest leaving call...');
  
  Object.values(peerConnections.current).forEach((pc) => {
    if (pc.signalingState !== 'closed') {
      pc.close();
    }
  });
  peerConnections.current = {};

  if (localStream) {
    localStream.getTracks().forEach((track) => track.stop());
    setLocalStream(null);
  }

  setRemoteStreams([]);
  
  Object.keys(remoteVideoRefs.current).forEach(userId => {
    if (remoteVideoRefs.current[userId]) {
      remoteVideoRefs.current[userId].srcObject = null;
    }
  });
  remoteVideoRefs.current = {};

  if (socketRef.current) {
    socketRef.current.emit('leaving-room', room);
    socketRef.current.disconnect();
    socketRef.current = null;
  }
  
  setIsJoinedCall(false);
  setIsModalOpen(true);
  setGuestName('');
  setIsWaitingForAdmission(false);
  setRemoteMutedStates({});
  
  console.log('Guest successfully left call and cleaned up resources');
};

const toggleVideo = () => {
  if (localStream) {
    localStream.getVideoTracks().forEach((track) => {
      track.enabled = !track.enabled;
      setIsVideoEnabled(track.enabled);
    });
  }
};

const toggleAudio = () => {
  if (localStream) {
    localStream.getAudioTracks().forEach((track) => {
      track.enabled = !track.enabled;
      setIsAudioEnabled(track.enabled);

      if (socketRef.current) {
        socketRef.current.emit('guest-audio-toggle', {
          room,
          userId: socketRef.current.id,
          isMuted: !track.enabled,
        });
      }
    });
  }
};

const handleFullscreen = (videoClass) => {
  const videoElement =
    videoClass === 'host-video'
      ? document.querySelector(`.${videoClass}`)
      : remoteVideoRefs.current[videoClass];

  if (videoElement) {
    if (videoElement.requestFullscreen) {
      videoElement
        .requestFullscreen()
        .catch((error) => console.error('Error trying to enter full screen mode:', error));
    } else if (videoElement.webkitEnterFullscreen) {
      videoElement.webkitEnterFullscreen();
    } else {
      console.warn('Fullscreen API is not supported on this device.');
    }
  }
};

return (
  <>
    <div className="conference-container">
      <h1><center>AirFlyp 1:1 Guest Conference</center></h1>
      {!isModalOpen && !isWaitingForAdmission && (
        <>
          <div className="controls">
            <button onClick={joinCall} disabled={isJoinedCall}>
              {isJoinedCall ? 'Joined Call' : 'Join Call'}
            </button>
            <button onClick={leaveCall}>Leave Call</button>
            <button onClick={toggleVideo}>
              {isVideoEnabled ? 'Turn Video Off' : 'Turn Video On'}
            </button>
            <button onClick={toggleAudio}>
              {isAudioEnabled ? 'Mute' : 'Unmute'}
            </button>
          </div>
          
          <div className={`videos ${!isModalOpen && isJoinedCall ? 'show' : ''}`}>
            <div className={`video-grid ${
              !hostStream && remoteStreams.length === 0 ? 'guest-only' :
              (hostStream ? 1 : 0) + remoteStreams.length + 1 === 2 ? 'two-videos' : 'multiple-videos'
            }`}>
              
              {/* Host Video */}
              {hostStream?.stream && (
                <div className="video-wrapper">
                  <video
                    ref={(el) => {
                      if (el && !el.srcObject) el.srcObject = hostStream.stream;
                    }}
                    autoPlay
                    playsInline
                    className="host-video"
                    onClick={() => handleFullscreen('host-video')}
                  />
                  <p className="video-name">Host: {hostStream.name || 'Host'}</p>
                </div>
              )}

              {/* Guest's Own Video */}
              <div className="self-video video-wrapper">
                <video
                  ref={localVideoRef}
                  autoPlay
                  muted
                  playsInline
                  className="local-video"
                  onClick={() => handleFullscreen('guest-video')}
                />
                {!isAudioEnabled && (
                  <div className="mute-overlay">
                    <p>Muted</p>
                  </div>
                )}
                <p className="video-name">You</p>
              </div>

              {/* Other Remote Guests */}
              {remoteStreams.map(({ userId, stream, name }) => (
                <div key={userId} className="video-wrapper">
                  {stream && (
                    <video
                      ref={(el) => {
                        remoteVideoRefs.current[userId] = el;
                        if (el && !el.srcObject) el.srcObject = stream;
                      }}
                      autoPlay
                      playsInline
                      className="remote-video"
                      onClick={() => handleFullscreen(userId)}
                    />
                  )}
                  <p className="video-name">{name || 'Guest'}</p>
                  {remoteMutedStates[userId] && (
                    <div className="mute-overlay">
                      <p>Muted</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>

    {callEnded && (
  <div className="call-ended-overlay" style={{
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    color: 'white',
    flexDirection: 'column',
    textAlign: 'center'
  }}>
    <h2>Call Ended</h2>
    <p>{callEndReason}</p>
    <p>Thank you for joining!</p>
    <button 
      onClick={() => window.location.href = 'https://www.airflyp.com'}
      style={{
        padding: '10px 20px',
        marginTop: '20px',
        backgroundColor: '#345B63',
        color: 'white',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer'
      }}
    >
      Return to Home
    </button>
  </div>
)}

    {/* UPDATED MODAL */}
    {isModalOpen && (
      <>
        <div className="modal-backdrop" />
        <div className="modal">
          <input
            type="text"
            placeholder="Please enter your name"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && guestName.trim()) {
                requestAdmission();
              }
            }}
          />
          <button
            onClick={requestAdmission}
            disabled={!guestName.trim()}
          >
            Request to Join
          </button>
        </div>
      </>
    )}

    {/* WAITING STATE */}
    {isWaitingForAdmission && (
      <div className="waiting-message" style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'white',
        padding: '20px',
        borderRadius: '10px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
        zIndex: 1000
      }}>
        <h2>Waiting for host to admit you...</h2>
        <p>Host will review your request to join as: <strong>{guestName}</strong></p>
        <p>Please wait...</p>
      </div>
    )}
  </>
);
};

export default GuestConference;